<script>
  import {
    PlayFill,
    PauseFill,
    SkipEndFill,
    VolumeUpFill,
    VolumeMuteFill,
    Shuffle,
  } from "svelte-bootstrap-icons/lib/";
  import { fly } from "svelte/transition";
  import { tracks, playingTrack } from "../stores.js";

  let paused = true;
  let currentTime = 0;
  let duration = 0;
  let muted = false;
  let random = false;
  let seeking = false;
  let waiting = false;

  function loadedData() {
    paused = false;
  }

  function loadStart() {
    paused = true;
    currentTime = 0;
  }

  function togglePlay() {
    paused = !paused;
  }

  function toggleMuted() {
    muted = !muted;
  }

  function toggleRandom() {
    random = !random;
  }

  function ended() {
    playNext();
  }

  function getNextTrack() {
    if (!tracks || !playingTrack) return;
    const index = $tracks.findIndex(
      (d) =>
        d.name + d.identifier === $playingTrack.name + $playingTrack.identifier
    );

    let nextIndex = index + 1;

    if (random) {
      const randomArray = Array.from(Array($tracks.length).keys()).filter(
        (d) => d !== index
      );
      const randomIndex = Math.floor(Math.random() * randomArray.length);
      nextIndex = randomArray[randomIndex];
    }

    return nextIndex > $tracks.length ? 0 : nextIndex;
  }

  function playNext() {
    playingTrack.set($tracks[getNextTrack()]);
  }

  function seek(e, element) {
    const { left, right } = element.getBoundingClientRect();
    currentTime = (duration * (e.clientX - left)) / (right - left);
  }

  function handleMousemove(e) {
    if (!(e.buttons & 1)) return; // mouse not down
    if (!duration) return; // video not loaded yet

    seeking = true;
    seek(e, this);
  }

  function handleMousedown(e) {
    function handleMouseup() {
      if (duration) {
        seek(e, this);
      }
      seeking = false;
      cancel();
    }

    function cancel() {
      e.target.removeEventListener("mouseup", handleMouseup);
    }

    e.target.addEventListener("mouseup", handleMouseup);

    setTimeout(cancel, 200);
  }

  function formatSeconds(seconds) {
    if (isNaN(seconds)) return "No Data";
    var sec_num = parseInt(seconds, 10);
    var hours = Math.floor(sec_num / 3600);
    var minutes = Math.floor(sec_num / 60) % 60;
    var seconds = sec_num % 60;
    return (
      [hours, minutes, seconds]
        .map((v) => (v < 10 ? "0" + v : v))
        //.filter((v, i) => v !== "00" || i > 0)
        .join(":")
    );
  }

  function toggleWaiting(value) {
    waiting = value;
  }
</script>

<style>
  .playerBg {
    position: sticky;
    bottom: 0;
    width: 100%;
    height: auto;
    background-color: #f8f9fa!important;
    color: black!important;
  }

  .timer {
    font-size: 0.75rem;
  }

  .playerBg :global(.playerButton) {
    width: 30px;
    height: 30px;
  }

  .info {
    align-items: unset;
    text-align: center;
  }

  @media (min-width: 768px) {
    .playerBg {
      height: 100px;
    }

    .playerBg :global(.playerButton) {
      width: 50px;
      height: 50px;
    }

    .info {
      align-items: center;
      text-align: left;
    }
  }
</style>

{#if $playingTrack}
  <div
    transition:fly={{ opacity: 1, duration: 1000, y: 100 }}
    class="playerBg bg-secondary border-top border-primary d-flex align-items-center pb-2 pb-md-0">
    <div class="container-fluid">
      <div
        class="playerContainer align-items-center justify-content-between d-flex flex-wrap flex-md-nowrap">
        <div class="px-2 flex-grow-0 flex-shrink-0 order-1">
          <button class="btn btn-light" on:click={togglePlay}>
            {#if paused}
              <PlayFill class="playerButton" />
            {:else}
              <PauseFill class="playerButton" />
            {/if}
          </button>
        </div>
        <div class="px-2 flex-grow-0 flex-shrink-0 order-1">
          <button class="btn btn-light" on:click={playNext}>
            <SkipEndFill class="playerButton" />
          </button>
        </div>

        <div class="px-2 flex-grow-1 order-0 order-md-1 overflow-hidden w-100">
          <!-- svelte-ignore a11y-media-has-caption -->
          <audio
            bind:paused
            bind:duration
            bind:currentTime
            bind:muted
            on:ended={ended}
            on:loadeddata={loadedData}
            on:loadstart={loadStart}
            on:waiting={() => toggleWaiting(true)}
            on:canplaythrough={() => toggleWaiting(false)}
            preload="auto"
            src={`https://archive.org/download/${$playingTrack.identifier}/${$playingTrack.name}`} />

          <div class="d-flex flex-column">
            <div
              class:progress-bar-striped={waiting}
              class:progress-bar-animated={waiting}
              class="progress w-100 mt-0 mt-md-2 mb-3 mb-md-0 order-1"
              style="height: 10px; background-color:#ced4da;"
              on:mousemove={handleMousemove}
              on:mousedown={handleMousedown}>
              <div
                class="progress-bar"
                role="progressbar"
                style={`transition: none;pointer-events:none; width:${(currentTime * 100) / duration}%`}
                aria-valuenow={currentTime || 0}
                aria-valuemin="0"
                aria-valuemax={duration} />
            </div>
            <div
              class="info d-flex flex-column flex-md-row my-2 order-0 order-md-1 overflow-hidden">
              <div class="overflow-hidden">
                <p class="my-0 mr-2 text-truncate">
                  <span class="purple">{$playingTrack.identifierTitle}</span>
                  •
                  {$playingTrack.title}
                </p>
              </div>
              <div class="ml-md-auto">
                <p class="m-0  text-monospace text-black-50 timer">
                  {formatSeconds(currentTime)}/{formatSeconds(duration)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="px-2 flex-grow-0 flex-shrink-0 order-1">
          <button class="btn btn-light" on:click={toggleRandom}>
            <Shuffle fill={random ? 'black' : 'gray'} class="playerButton" />
          </button>
        </div>
        <div class="px-2 flex-grow-0 flex-shrink-0 order-1">
          <button class="btn btn-light" on:click={toggleMuted}>
            {#if muted}
              <VolumeMuteFill class="playerButton" />
            {:else}
              <VolumeUpFill class="playerButton" />
            {/if}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}
